import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BarChartIcon from '@mui/icons-material/BarChart';
import GetAppIcon from '@mui/icons-material/GetApp';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ViewListIcon from '@mui/icons-material/ViewList';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import {
  getSecurityEndpointDetailsHref,
  getSecurityMitreDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type {
  WazuhMitreEndpointItem,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrGlobalScopeFilters,
  EdrKpiCard,
  EdrPageHeader,
  EdrStatusPill,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';
import {
  getMitreAffectedEndpointListColumns,
  getMitreTechniqueId,
  getMitreTechniqueListColumns,
  MITRE_SEVERITY_FILTER_OPTIONS,
} from './mitreDetailModel';
import { useMitreData, useMitreExport, type MitreSortParams } from './useMitreData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'MITRE ATT&CK', to: getSecurityNavItemPath('mitre-attck') ?? '' },
  ],
};


const MitreAttck: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.mitreAttck);
  const {
    tactic = '',
    technique = '',
    severityBand = '',
    mitreId = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [endpointsSortParams, setEndpointsSortParams] = useState<MitreSortParams | null>(null);
  const [resetEndpointsSortFlag, setResetEndpointsSortFlag] = useState(false);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'mitre-attck',
  );
  const canSummary = canWidget('summary');
  const canTechniquesList = canWidget('techniquesList');
  const canTopTechniques = canWidget('topTechniques');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const showUnavailable =
    permissionsResolved &&
    !canSummary &&
    !canTechniquesList &&
    !canTopTechniques &&
    !canAffectedEndpoints;

  const mitreFilters = useMemo(
    () => ({
      tactic: filters.tactic,
      technique: filters.technique,
      mitreId: filters.mitreId,
      severityBand: filters.severityBand,
      organizationId,
    }),
    [filters.tactic, filters.technique, filters.mitreId, filters.severityBand, organizationId]
  );

  const { loading, summary, items: paged, endpoints: endpointsPaged, topTechniques, facets } =
    useMitreData(page, rowsPerPage, search, mitreFilters, endpointsSortParams);
  const { exporting, handleExport } = useMitreExport(search, mitreFilters);

  const openTechniqueDetail = useCallback(
    (item: WazuhMitreTechniqueItem) => {
      const id = getMitreTechniqueId(item);
      if (!id) return;
      navigate(getSecurityMitreDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  const openEndpointDetails = useCallback(
    (item: WazuhMitreEndpointItem) => {
      const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
      if (!systemWazuhAgentId) return;
      navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
        state: { endpointName: item.endpoint ?? item.name },
      });
    },
    [navigate]
  );

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const affectedEndpoints = endpointsPaged?.items ?? [];
  const affectedEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );

  const handleEndpointsSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc' | null) => {
      setEndpointsSortParams(direction ? { column: columnId, sort: direction } : null);
    },
    []
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'tactic',
        label: 'Tactic',
        options: facets.tactics.map((name) => ({ label: name, value: name })),
        value: tactic,
        multiple: true,
        onChange: (value) => {
          setFilter('tactic', value);
          setPage(0);
        },
      },
      {
        id: 'technique',
        label: 'Technique',
        options: facets.techniques.map((name) => ({ label: name, value: name })),
        value: technique,
        multiple: true,
        onChange: (value) => {
          setFilter('technique', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: MITRE_SEVERITY_FILTER_OPTIONS,
        value: severityBand,
        multiple: true,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'mitreId',
        label: 'MITRE ID',
        options: facets.mitreIds.map((id) => ({ label: id, value: id })),
        value: mitreId,
        multiple: true,
        onChange: (value) => {
          setFilter('mitreId', value);
          setPage(0);
        },
      },
    ],
    [
      tactic,
      technique,
      severityBand,
      mitreId,
      facets.tactics,
      facets.techniques,
      facets.mitreIds,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(() => getMitreTechniqueListColumns('global'), []);
  const affectedEndpointColumns: Column[] = useMemo(
    () => getMitreAffectedEndpointListColumns(),
    []
  );

  const topTechniqueColumns: Column[] = useMemo(
    () => [
      {
        id: 'label',
        label: 'Technique',
        render: (row) => {
          const item = row as unknown as WazuhMitreTechniqueItem;
          return (
            <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
              {[item.mitreId, item.techniqueName].filter(Boolean).join(' ') || '—'}
            </span>
          );
        },
      },
      {
        id: 'count',
        label: 'Count',
        align: 'right',
        render: (row) => (
          <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
            {formatEdrNumber((row as unknown as WazuhMitreTechniqueItem).detections)}
          </span>
        ),
      },
    ],
    []
  );

  const listToolbar = (
    <EdrDataToolbar
      searchValue={searchInput}
      onSearchChange={(value) => {
        setSearchInput(value);
        setPage(0);
      }}
      searchPlaceholder="Search Techniques…"
      filters={toolbarFilters}
      onReset={() => {
        handleResetFilters();
        setEndpointsSortParams(null);
        setResetEndpointsSortFlag((prev) => !prev);
      }}
    />
  );

  const topTechniquesToolbar = (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <ViewListIcon style={{ fontSize: 16, color: EDR_COLORS.brand500 }} />
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: EDR_COLORS.textPrimary,
        }}
      >
        Top Observed Techniques
      </Typography>
    </Box>
  );

  const affectedEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography style={{ fontSize: 13, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        Affected Endpoints
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(affectedEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

    return (
        <>
            <Helmet>
                <title>{getPageTitle('Security — MITRE ATT&CK')}</title>
            </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="MITRE ATT&CK"
          subtitle="Analyze attacker tactics and techniques detected across your protected environment."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canTechniquesList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.mitreAttck" />
        ) : null}

        {canSummary ? (
        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Active Techniques"
            value={formatEdrNumber(summary?.activeTechniques)}
            icon={<TrackChangesIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
            accent={EDR_COLORS.brand500}
          >
            Currently observed
          </EdrKpiCard>
          <EdrKpiCard
            label="High-Risk Techniques"
            value={formatEdrNumber(summary?.highRiskTechniques)}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          >
            Require immediate investigation
          </EdrKpiCard>
          <EdrKpiCard
            label="Total Detections"
            value={formatEdrNumber(summary?.totalDetections)}
            icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
          />
          <EdrKpiCard
            label="Detections by Severity"
            value={(
              <Box display="flex" flexWrap="wrap" style={{ gap: 6, fontSize: 11, fontWeight: 500 }}>
                <EdrStatusPill
                  tone="critical"
                  label={`${formatEdrNumber(summary?.detectionsBySeverity?.critical)} Critical`}
                  dot={false}
                />
                <EdrStatusPill
                  tone="high"
                  label={`${formatEdrNumber(summary?.detectionsBySeverity?.high)} High`}
                  dot={false}
                />
                <EdrStatusPill
                  tone="medium"
                  label={`${formatEdrNumber(summary?.detectionsBySeverity?.medium)} Medium`}
                  dot={false}
                />
                <EdrStatusPill
                  tone="success"
                  label={`${formatEdrNumber(summary?.detectionsBySeverity?.low)} Low`}
                  dot={false}
                />
                            </Box>
            )}
            icon={<BarChartIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
          />
                            </Box>
        ) : null}

        {canAffectedEndpoints ? (
          <EdrDataCard toolbar={affectedEndpointsToolbar}>
            <Table
              columns={affectedEndpointColumns}
              data={affectedEndpoints as unknown as TableData[]}
              loading={loading}
              showPagination={false}
              onSort={handleEndpointsSort}
              resetSortFlag={resetEndpointsSortFlag}
              onRowClick={(row) => openEndpointDetails(row as unknown as WazuhMitreEndpointItem)}
              TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            />
          </EdrDataCard>
        ) : null}

        {canTechniquesList ? (
        <EdrDataCard toolbar={listToolbar}>
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            onRowClick={(row) => openTechniqueDetail(row as unknown as WazuhMitreTechniqueItem)}
          />
        </EdrDataCard>
        ) : null}

        {canTopTechniques ? (
        <EdrDataCard toolbar={topTechniquesToolbar}>
          <Table
            columns={topTechniqueColumns}
            data={topTechniques as unknown as TableData[]}
            loading={false}
            showPagination={false}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            onRowClick={(row) => openTechniqueDetail(row as unknown as WazuhMitreTechniqueItem)}
          />
        </EdrDataCard>
        ) : null}
            </Box>
        </>
    );
};

export default MitreAttck;
